import React, { useMemo, useCallback } from "react";
// import { motion, AnimatePresence, LayoutGroup } from "framer-motion";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  horizontalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { X, GripHorizontal } from "lucide-react";
import { chordToRomanNumeral, parseChordName } from "../../lib/musicTheory";
import { type NoteName, noteToSemitones } from "../../lib/guitarEngine";

// ============================================
// Utilidad: color por función tonal
// ============================================

interface FunctionColor {
  color: string;
  bg: string;
  label: string;
}

export function getFunctionColor(chordName: string, key: NoteName): FunctionColor {
  if (!chordName) {
    return { color: "#94a3b8", bg: "rgba(255,255,255,0.06)", label: "—" };
  }
  const parsed = parseChordName(chordName);
  if (!parsed) {
    return { color: "#94a3b8", bg: "rgba(255,255,255,0.06)", label: "—" };
  }

  const rootSemi = noteToSemitones(parsed.root);
  const keySemi = noteToSemitones(key);
  const interval = ((rootSemi - keySemi) % 12 + 12) % 12;

  switch (interval) {
    case 0:
    case 9:
      return { color: "#FBBF24", bg: "rgba(251, 191, 36, 0.14)", label: "Tónica" };
    case 2:
    case 5:
      return { color: "#FB923C", bg: "rgba(251, 146, 60, 0.14)", label: "Subdominante" };
    case 7:
    case 11:
      return { color: "#F87171", bg: "rgba(248, 113, 113, 0.14)", label: "Dominante" };
    default:
      return { color: "#94a3b8", bg: "rgba(255,255,255,0.06)", label: "Otro" };
  }
}

// ============================================
// Slot sortable individual
// ============================================

interface SlotProps {
  id: string;
  chord: string;
  roman: string;
  index: number;
  isActive: boolean;
  keySignature: NoteName;
  onRemove: () => void;
  onChange: (value: string) => void;
}

const SortableSlot: React.FC<SlotProps> = ({
  id,
  chord,
  roman,
  index,
  isActive,
  keySignature,
  onRemove,
  onChange,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : "auto",
  };

  const { color, bg, label } = getFunctionColor(chord, keySignature);

  return (
    <div
      ref={setNodeRef}
      className={`relative h-[70px] min-w-[68px] rounded-xl flex flex-col items-center justify-between select-none snap-center transition-all duration-200 ${
        isActive
          ? "shadow-lg scale-[1.02]"
          : "shadow-sm hover:shadow-md"
      } ${isDragging ? "opacity-80 rotate-2" : ""}`}
      style={{
        ...style,
        backgroundColor: bg,
        border: `1px solid ${isActive ? color : "rgba(255,255,255,0.12)"}`,
        borderBottomWidth: isActive ? "3px" : "1px",
        borderBottomColor: isActive ? color : "rgba(255,255,255,0.12)",
      }}
    >
      {/* Drag handle */}
      <button
        {...attributes}
        {...listeners}
        className="absolute top-1 right-1 text-slate-500 hover:text-slate-300 cursor-grab active:cursor-grabbing z-10"
      >
        <GripHorizontal className="w-2.5 h-2.5" />
      </button>

      {/* Remove */}
      {chord && (
        <button
          onClick={onRemove}
          className="absolute bottom-1 right-1 text-slate-500 hover:text-red-400 transition-colors z-10"
        >
          <X className="w-2.5 h-2.5" />
        </button>
      )}

      {/* Contenido */}
      <div className="relative z-10 flex flex-col items-center justify-between h-full w-full pt-1.5 pb-1.5 px-2">
        {/* Roman numeral */}
        <span
          className="text-[9px] font-semibold tracking-wider uppercase leading-none"
          style={{ color }}
        >
          {roman}
        </span>

        {/* Chord input */}
        <input
          value={chord}
          onChange={(e) => onChange(e.target.value)}
          className="w-full text-center text-sm font-bold text-slate-100 bg-transparent border-none focus:outline-none focus:ring-1 focus:ring-white/25 rounded px-0 py-0 placeholder:text-slate-500 font-serif"
          placeholder="—"
        />

        {/* Function label + beats */}
        <span className="text-[8px] text-slate-400 font-sans leading-none">
          {label}
        </span>
      </div>
    </div>
  );
};

// ============================================
// Componente principal
// ============================================

export interface ProgressionSequencerProps {
  progression: string[];
  keySignature?: string;
  onChange: (progression: string[]) => void;
  activeIndex?: number;
}

export const ProgressionSequencer: React.FC<ProgressionSequencerProps> = ({
  progression,
  keySignature = "C",
  onChange,
  activeIndex = -1,
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // Mostrar todos los slots hasta el último no-vacío, más uno vacío al final
  const items = useMemo(() => {
    const lastNonEmpty = progression.map((c) => c.trim()).findLastIndex((c) => c !== "");
    const count = Math.max(1, lastNonEmpty + 1) + 1; // +1 slot vacío para agregar
    return Array.from({ length: count }, (_, i) => ({
      id: `slot-${i}`,
      chord: progression[i] || "",
      index: i,
    }));
  }, [progression]);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const oldIndex = items.findIndex((i) => i.id === active.id);
      const newIndex = items.findIndex((i) => i.id === over.id);
      if (oldIndex < 0 || newIndex < 0 || oldIndex >= items.length || newIndex >= items.length) return;

      const newProgression = arrayMove(
        items.map((i) => i.chord),
        oldIndex,
        newIndex
      );
      onChange(newProgression);
    },
    [items, onChange]
  );

  const updateChord = useCallback(
    (index: number, value: string) => {
      const newProgression = [...items.map((i) => i.chord)];
      newProgression[index] = value;
      onChange(newProgression);
    },
    [items, onChange]
  );

  const removeSlot = useCallback(
    (index: number) => {
      const newProgression = items.map((i) => i.chord);
      newProgression.splice(index, 1);
      // Filtrar vacíos al final
      while (newProgression.length > 0 && !newProgression[newProgression.length - 1]) {
        newProgression.pop();
      }
      onChange(newProgression);
    },
    [items, onChange]
  );

  return (
    <div>
      {/* Sequencer grid — centrado con scroll horizontal */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={items.map((i) => i.id)}
          strategy={horizontalListSortingStrategy}
        >
          <div className="w-full flex justify-center">
            <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-thin px-1 snap-x snap-mandatory">
              {items.map((item, idx) => (
                <SortableSlot
                  key={item.id}
                  id={item.id}
                  chord={item.chord}
                  roman={item.chord ? chordToRomanNumeral(item.chord, keySignature as NoteName) : "—"}
                  index={idx}
                  isActive={activeIndex === idx}
                  keySignature={keySignature as NoteName}
                  onRemove={() => removeSlot(idx)}
                  onChange={(val) => updateChord(idx, val)}
                />
              ))}
            </div>
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
};

export default ProgressionSequencer;
